import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import BrandfetchGlassmorphicDisplay from '../components/BrandfetchGlassmorphicDisplay';
import { authenticatedFetch } from '../utils/apiUtils';

/**
 * A standalone page for brands explorer that can be accessed directly
 */
const StandaloneBrandsExplorer = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const checkAuthAndFetchBrands = async () => {
      try {
        setLoading(true);
        
        // Check if user is authenticated and has admin rights
        const authResponse = await fetch('/api/auth/me');
        if (!authResponse.ok) {
          window.location.href = '/'; // Redirect to home/login if not authenticated
          return;
        }
        
        const userData = await authResponse.json();
        if (!userData || !userData.is_admin) {
          toast.error('Admin access required');
          window.location.href = '/admin';
          return;
        }
        
        // User is authenticated and is admin, fetch brands
        const brandResponse = await authenticatedFetch(`/api/admin/brands?userId=${userData.id}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (!brandResponse.ok) {
          throw new Error('Failed to fetch brands data');
        }
        
        const brandsData = await brandResponse.json();
        setBrands(brandsData || []);
      } catch (err) {
        console.error('Error:', err);
        setError(err.message || 'An error occurred');
        toast.error('Failed to load brands data');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthAndFetchBrands();
  }, []);

  const openGlassmorphicView = (brand) => {
    setSelectedBrand(brand);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedBrand(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center h-64">
            <div className="text-gray-500 flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading brands data...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">Error: {error}</p>
                <button onClick={() => window.location.reload()} className="mt-2 text-sm font-medium text-red-700 underline">Try Again</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Brands Explorer - Glassmorphic View</h1>
            <div className="flex space-x-4">
              <button 
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
                Refresh Data
              </button>
              <button 
                onClick={() => window.location.href = '/admin'}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back to Admin
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <p className="mb-6 text-gray-600">
              Select a brand below to view its AI insights and analysis in the new glassmorphic interface.
            </p>
            
            {brands.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <p className="text-gray-500">No brands found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {brands.map((brand) => (
                  <div 
                    key={brand.id} 
                    className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="p-4">
                      <h3 className="text-lg font-semibold mb-2">{brand.name || brand.domain}</h3>
                      <p className="text-gray-600 mb-4 truncate">{brand.domain}</p>
                      
                      <button
                        onClick={() => openGlassmorphicView(brand)}
                        className="w-full bg-gradient-to-r from-teal-500 to-green-500 text-white py-2 px-4 rounded-md hover:from-teal-600 hover:to-green-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm1 0v12h12V4H4z" clipRule="evenodd" />
                          <path d="M6 7a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h5a1 1 0 100-2H7z" />
                        </svg>
                        Glassmorphic View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Render the glassmorphic modal if a brand is selected */}
      {showModal && selectedBrand && (
        <BrandfetchGlassmorphicDisplay 
          domain={selectedBrand.domain} 
          onClose={closeModal} 
        />
      )}
    </div>
  );
};

export default StandaloneBrandsExplorer; 